import { useEffect, useMemo } from 'react';
import { formatDateTime } from '../../lib/locale';
import { Link, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useStore } from 'zustand';
import { ArrowLeft } from 'lucide-react';
import { BUILTIN_CATALOG, memberCan, newId } from '@stageplottter/core';
import { useMe } from '../../shell/me';
import { DataGrid } from '../tables/grid/DataGrid';
import {
  applyInventoryEdit,
  deleteInventoryRows,
  insertInventoryRow,
  inventoryColumns,
  totalPieces,
} from './model/inventory';
import { inventoryStore } from './store';
import './inventory.css';

const CATALOG = BUILTIN_CATALOG.entries;

/** Workspace inventory page `/w/:wsId/inventory` (decision U15). */
export function InventoryPage() {
  const { t, i18n } = useTranslation();
  const { wsId = '' } = useParams();
  const me = useMe();
  const role =
    me.status === 'ready' ? me.me.workspaces.find((w) => w.id === wsId)?.role : undefined;
  const canEdit = role !== undefined && memberCan(role, 'inventory.edit');
  const s = useStore(inventoryStore);
  const columns = useMemo(() => inventoryColumns(t, CATALOG), [t]);

  useEffect(() => {
    void inventoryStore.getState().load(wsId);
  }, [wsId]);
  useEffect(() => {
    // leaving the page saves pending edits right away
    const onHide = () => void inventoryStore.getState().flush();
    window.addEventListener('pagehide', onHide);
    return () => {
      window.removeEventListener('pagehide', onHide);
      void inventoryStore.getState().flush();
    };
  }, []);

  const saveState = s.saving
    ? t('inventory.saving')
    : s.dirty
      ? t('inventory.unsaved')
      : s.status === 'error'
        ? t('inventory.saveFailed')
        : t('inventory.saved');

  return (
    <div className="page inventory-page">
      <div className="page-head">
        <Link className="btn btn-ghost" to={`/w/${encodeURIComponent(wsId)}`}>
          <ArrowLeft size={15} aria-hidden="true" /> {t('inventory.back')}
        </Link>
        <h1>{t('inventory.title')}</h1>
        <span className="muted small" role="status">
          {saveState}
        </span>
      </div>
      <p className="muted">{t('inventory.intro')}</p>
      {!canEdit && <p className="muted small">{t('inventory.readOnly')}</p>}
      {s.conflict && (
        <p className="inventory-conflict" role="alert">
          {t('inventory.conflict')}{' '}
          <button
            type="button"
            className="btn btn-small"
            onClick={() => inventoryStore.getState().dismissConflict()}
          >
            OK
          </button>
        </p>
      )}
      {s.status === 'unavailable' && <p role="alert">{t('inventory.unavailable')}</p>}
      {s.status === 'loading' && <p className="muted">{t('common.loading')}</p>}
      {(s.status === 'ready' || s.status === 'error') && (
        <>
          <p className="muted small">
            {t('inventory.summary', { entries: s.entries.length, pieces: totalPieces(s.entries) })}
            {s.updatedAt && s.updatedBy
              ? ` · ${t('inventory.updatedBy', {
                  when: formatDateTime(s.updatedAt, i18n.language),
                  name: s.updatedBy,
                })}`
              : ''}
          </p>
          <div className="inventory-grid">
            <DataGrid
              commentable={false}
              id="inventory"
              label={t('inventory.gridLabel')}
              rows={s.entries}
              rowKey={(r) => r.id}
              columns={columns}
              canEdit={canEdit}
              allowAppend={canEdit}
              emptyText={t('inventory.empty')}
              onEdit={(edit) =>
                inventoryStore
                  .getState()
                  .edit((entries) => applyInventoryEdit(entries, edit, CATALOG, () => newId()))
              }
              onInsertRow={(rowKey, where) =>
                inventoryStore
                  .getState()
                  .edit((entries) =>
                    insertInventoryRow(
                      entries,
                      rowKey,
                      where,
                      () => newId(),
                      t('inventory.newEntry'),
                    ),
                  )
              }
              onDeleteRows={(keys) =>
                inventoryStore.getState().edit((entries) => deleteInventoryRows(entries, keys))
              }
            />
          </div>
        </>
      )}
    </div>
  );
}
